/**
 * RoomForm Component - Create/Edit bingo rooms
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import { ROUTES, SUCCESS_MESSAGES } from '../../utils/constants';
import './Rooms.css';

const RoomForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    maxPlayers: 20,
    entryFee: 50,
    prizePool: 500,
    cardPrice: 50,
    maxCardsPerPlayer: 4,
    roomType: 'public',
    isActive: true,
    autoStartEnabled: true,
    minPlayersToStart: 2,
    callInterval: 5000,
  });

  useEffect(() => {
    if (isEdit) {
      loadRoom();
    }
  }, [id]);

  const loadRoom = async () => {
    try {
      setLoading(true);
      const data = await api.getRoomById(id);
      if (data.room) {
        setFormData({
          name: data.room.name || '',
          maxPlayers: data.room.maxPlayers || 20,
          entryFee: data.room.entryFee || 50,
          prizePool: data.room.prizePool || 500,
          cardPrice: data.room.cardPrice || 50,
          maxCardsPerPlayer: data.room.maxCardsPerPlayer || 4,
          roomType: data.room.roomType || 'public',
          isActive: data.room.isActive !== false,
          autoStartEnabled: data.room.autoStartEnabled !== false,
          minPlayersToStart: data.room.minPlayersToStart || 2,
          callInterval: data.room.callInterval || 5000,
        });
      }
    } catch (error) {
      toast.error('Error al cargar sala');
      navigate(ROUTES.ROOMS);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      setLoading(true);
      
      const dataToSend = {
        ...formData,
        hostId: user?._id || user?.id,
      };

      if (isEdit) {
        await api.updateRoom(id, dataToSend);
        toast.success('Sala actualizada correctamente');
      } else {
        await api.createRoom(dataToSend);
        toast.success(SUCCESS_MESSAGES.ROOM_CREATED || 'Sala creada correctamente');
      }
      
      navigate(ROUTES.ROOMS);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar sala');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="room-form">
      <Card
        title={isEdit ? 'Editar Sala' : 'Nueva Sala'}
        subtitle={isEdit ? 'Modifica los datos de la sala' : 'Crea una nueva sala de bingo'}
      >
        <form onSubmit={handleSubmit} className="form-grid">
          <div className="form-group">
            <label htmlFor="name">Nombre de la Sala *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej: Sala Premium"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="roomType">Tipo de Sala</label>
            <select id="roomType" name="roomType" value={formData.roomType} onChange={handleChange}>
              <option value="public">Pública</option>
              <option value="beginner">Principiante</option>
              <option value="quick">Rápida</option>
              <option value="premium">Premium</option>
              <option value="vip">VIP</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="maxPlayers">Máximo de Jugadores</label>
            <input
              type="number"
              id="maxPlayers"
              name="maxPlayers"
              value={formData.maxPlayers}
              onChange={handleChange}
              min="2"
              max="100"
            />
          </div>

          <div className="form-group">
            <label htmlFor="entryFee">Costo de Entrada</label>
            <input
              type="number"
              id="entryFee"
              name="entryFee"
              value={formData.entryFee}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="prizePool">Premio Total</label>
            <input
              type="number"
              id="prizePool"
              name="prizePool"
              value={formData.prizePool}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="cardPrice">Precio por Cartón</label>
            <input
              type="number"
              id="cardPrice"
              name="cardPrice"
              value={formData.cardPrice}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="maxCardsPerPlayer">Máx. Cartones por Jugador</label>
            <input
              type="number"
              id="maxCardsPerPlayer"
              name="maxCardsPerPlayer"
              value={formData.maxCardsPerPlayer}
              onChange={handleChange}
              min="1"
              max="10"
            />
          </div>

          <div className="form-group">
            <label htmlFor="minPlayersToStart">Mín. Jugadores para Iniciar</label>
            <input
              type="number"
              id="minPlayersToStart"
              name="minPlayersToStart"
              value={formData.minPlayersToStart}
              onChange={handleChange}
              min="1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="callInterval">Intervalo de Llamada (ms)</label>
            <input
              type="number"
              id="callInterval"
              name="callInterval"
              value={formData.callInterval}
              onChange={handleChange}
              min="1000"
              step="1000"
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              Sala Activa
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="autoStartEnabled"
                checked={formData.autoStartEnabled}
                onChange={handleChange}
              />
              Auto-inicio habilitado
            </label>
          </div>

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={() => navigate(ROUTES.ROOMS)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear Sala')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default RoomForm;
